import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { feedRequest } from "./api";

const merge = rows => [...new Map(rows.map(row => [row._id, row])).values()]
  .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b._id.localeCompare(a._id));
const pagePath = (path, cursor) => cursor ? `${path}${path.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(cursor)}` : path;

export default function useFeedPages(path, revision) {
  const client = useQueryClient();
  const { userId } = useAuth();
  const queryKey = useMemo(() => ["account", userId, "feed", path], [userId, path]);
  const [localError, setError] = useState("");
  const versions = useRef(new Map());
  const currentPath = useRef(path);
  currentPath.current = path;
  const lastRevision = useRef({ path, revision });
  const query = useInfiniteQuery({
    queryKey, initialPageParam: null, enabled: Boolean(userId),
    queryFn: ({ signal, pageParam }) => feedRequest(pagePath(path, pageParam), { signal }),
    getNextPageParam: lastPage => lastPage.nextCursor || undefined,
  });
  useEffect(() => {
    setError("");
    if (lastRevision.current.path === path && lastRevision.current.revision !== revision) {
      client.invalidateQueries({ queryKey, exact: true });
    }
    lastRevision.current = { path, revision };
  }, [client, queryKey, path, revision]);

  const remove = useCallback(id => {
    versions.current.set(`${path}:${id}`, Symbol());
    client.cancelQueries({ queryKey, exact: true });
    client.setQueryData(queryKey, previous => previous ? {
      ...previous, pages: previous.pages.map(page => ({ ...page, items: page.items.filter(row => row._id !== id) })),
    } : previous);
    client.invalidateQueries({ queryKey: ["account", userId, "feed"], type: "inactive", refetchType: "none" });
  }, [client, queryKey, userId, path]);

  const updateItem = useCallback(async (id, loader) => {
    const version = Symbol();
    const versionKey = `${path}:${id}`;
    versions.current.set(versionKey, version);
    try {
      const item = await loader();
      if (versions.current.get(versionKey) !== version || currentPath.current !== path) return;
      if (!client.getQueryData(queryKey)) {
        await client.invalidateQueries({ queryKey, exact: true });
        return;
      }
      // Cancel stale list responses before applying the authoritative item response.
      await client.cancelQueries({ queryKey, exact: true });
      if (versions.current.get(versionKey) !== version || currentPath.current !== path) return;
      client.setQueryData(queryKey, previous => {
        if (!previous) return previous;
        const pages = previous.pages.map(page => ({ ...page, items: page.items.filter(row => row._id !== id) }));
        if (item) {
          const index = Math.max(0, previous.pages.findIndex(page => page.items.some(row => row._id === id)));
          pages[index] = { ...pages[index], items: merge([...pages[index].items, item]) };
        }
        return { ...previous, pages };
      });
      client.invalidateQueries({ queryKey: ["account", userId, "feed"], type: "inactive", refetchType: "none" });
    } catch (error) {
      if (versions.current.get(versionKey) !== version || currentPath.current !== path) return;
      if (error.status === 404) remove(id);
      else {
        if ([401, 403].includes(error.status)) {
          await client.cancelQueries({ queryKey, exact: true });
          client.setQueryData(queryKey, { pages: [{ items: [], nextCursor: null }], pageParams: [null] });
          client.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
        }
        setError(error.message);
      }
    }
  }, [client, queryKey, userId, path, remove]);
  const denied = [401, 403, 404].includes(query.error?.status);
  const more = useCallback(() => {
    if (!query.isFetching && query.hasNextPage) query.fetchNextPage({ cancelRefetch: false });
  }, [query.isFetching, query.hasNextPage, query.fetchNextPage]);
  return {
    items: denied ? [] : merge(query.data?.pages.flatMap(page => page.items) || []),
    loading: query.isPending, loadingMore: query.isFetchingNextPage,
    refreshing: query.isFetching && !query.isFetchingNextPage,
    error: localError || query.error?.message || "",
    nextCursor: denied ? null : query.data?.pages.at(-1)?.nextCursor,
    more, updateItem, remove, retry: () => { setError(""); query.refetch(); },
  };
}
